import { type Message } from '@/features/messaging/Message'
import { type ActivationLifecycle } from '@/infra/activation'
import { Id } from '@/infra/id'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { action, computed } from 'mobx'
import type { ConversationStore } from './ConversationStore'
import { TextMessageContentViewModel } from './TextMessageContentViewModel'

/**
 * View model for a message's content, specific to its content type.
 */
export interface MessageContentViewModel extends Partial<ActivationLifecycle> {
  readonly message: Message
}

/**
 * View model for a message in a conversation.
 */
export class MessageViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  constructor(
    readonly message: Message,
    readonly content: MessageContentViewModel,
    private readonly _conversationStore: ConversationStore,
    private readonly _canEditMessages: () => boolean,
    private readonly _getEditingMessageId: () => Id,
    private readonly _setEditingMessageId: (messageId: Id) => void,
    private readonly _updateMessage: (
      messageId: Id,
      text: string,
    ) => Promise<unknown>,
    private readonly _isUpdatePending: () => boolean,
  ) {}

  /**
   * Message type.
   */
  @computed
  get messageType() {
    return this.message.messageType
  }

  /**
   * Message content type.
   */
  @computed
  get contentType() {
    return this.message.content.type
  }

  /**
   * Whether the message is a note.
   */
  @computed
  get isNote() {
    return this.messageType === 'Note'
  }

  /**
   * Whether the content is text. Step updates render as their own cards and
   * would look broken inside a coloured bubble.
   */
  @computed
  get isText() {
    return this.content instanceof TextMessageContentViewModel
  }

  /**
   * Checks if the message is editable by the message type.
   */
  @computed
  get isEditable() {
    return this.messageType === 'AdminMessage' || this.messageType === 'Message'
  }

  /**
   * Whether the current user may edit this message.
   */
  @computed
  get canEdit() {
    return this._canEditMessages() && this.isEditable
  }

  /**
   * Whether the message is in edit mode.
   */
  @computed
  get isEditing() {
    return this._getEditingMessageId() === this.message.id
  }

  /**
   * Whether a message edit is being saved.
   */
  @computed
  get isSavePending() {
    return this._isUpdatePending()
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    this.content.activate?.()
  }

  /** @inheritDoc */
  deactivate() {
    this.content.deactivate?.()
    this._lifecycle.deactivate()
  }

  /**
   * Puts the message in edit mode.
   */
  @action.bound
  startEdit() {
    this._setEditingMessageId(this.message.id)
  }

  /**
   * Leaves edit mode without saving.
   */
  @action.bound
  cancelEdit() {
    this._setEditingMessageId(Id.none)
  }

  /**
   * Saves the edited text of the message.
   */
  @action.bound
  saveEdit(text: string) {
    // Saving from the keyboard isn't awaited, and the update alerts its own
    // failure.
    return this._updateMessage(this.message.id, text).catch(() => undefined)
  }

  /**
   * Marks the message as read.
   */
  @action.bound
  markAsRead() {
    this._conversationStore.markAsRead(this.message)
  }
}
